import { Injectable } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { Basket } from '@df/session/api/basket/basket';
import { NFurnitureAssembly } from './furniture-assembly.namespace';

@Injectable({
    providedIn: 'root'
})
export class FurnitureAssemblyService {
    constructor(
        private basket: Basket,
        private productResource: ProductResource,
        private requestService: RequestService
    ) {}
    toggle(type: NFurnitureAssembly.EType, status?: Status): void {
        const path = this.isInBasket(type) ? NFurnitureAssembly.Endpoints.remove : NFurnitureAssembly.Endpoints.add;
        this.requestService.send(status, { path, data: { type } });
    }
    isInBasket(type: NFurnitureAssembly.EType): boolean {
        return !!this.getConfigItem(type)?.added;
    }
    loadProducts(status?: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): void {
        this.requestService.send(
            status,
            () => {
                return new Promise<void>((resolve, reject) => {
                    const skusToLoad = this.availableTypes.map(i => this.getProductSku(i));
                    if (skusToLoad.length) {
                        Promise.all(skusToLoad.map(i => this.productResource.find({ verbosity: 3, filter: { sku: i } })))
                            .then(() => {
                                resolve();
                            })
                            .catch(reject);
                    } else {
                        resolve();
                    }
                });
            },
            onSuccess,
            onError
        );
    }
    getAvailableAssemblyItems(): NFurnitureAssembly.IAssemblyItem[] {
        return this.availableTypes
            .map(type => {
                const product = this.getProduct(type);
                if (product?.isInStock()) {
                    return {
                        type,
                        product
                    };
                }
                return undefined;
            })
            .filter(i => !!i);
    }
    private isEligible(type: NFurnitureAssembly.EType): boolean {
        return !!this.getConfigItem(type)?.eligible;
    }
    private getProductSku(type: NFurnitureAssembly.EType): string | undefined {
        return this.getConfigItem(type)?.sku;
    }
    private getProduct(type: NFurnitureAssembly.EType): Product<IProductDataV3> | undefined {
        const sku = this.getProductSku(type);
        if (sku) {
            return this.productResource.getAllExisting().find(i => i.data?.sku === sku) as Product<IProductDataV3>;
        }
        return undefined;
    }
    private getConfigItem(type: NFurnitureAssembly.EType): NFurnitureAssembly.IBasketDataConfigurationItem | undefined {
        return this.basket.data?.assembly?.[type];
    }
    private get availableTypes(): NFurnitureAssembly.EType[] {
        return this.types.filter(i => this.isEligible(i));
    }
    private get types(): NFurnitureAssembly.EType[] {
        return Object.values(NFurnitureAssembly.EType);
    }
}
